/**
 * Task-router agent — the fourth producer that does *real* work, not simulation.
 *
 * The `task-router` template's blurb promises it "routes incoming work to the
 * right owner by skill and load, and assigns them." Until now nothing backed
 * that up. This module makes it honest: it reads the *real* ticket queue, finds
 * the high-priority work that is sitting *unowned* (nobody is on the hook), and
 * proposes a *real* `assign_ticket` write — routing each ticket to a human
 * owner — into the approvals inbox.
 *
 * It performs no write itself — it only proposes. The operator approves in the
 * Dashboard approvals inbox, and `resolveApproval` runs the action through
 * `runActionWithUndo`, which genuinely sets the ticket's owner (appending an
 * `assigned` timeline event) and is undoable. So the loop is honest end-to-end:
 *
 *   real data  →  real proposal  →  operator approval  →  real store write
 *
 * Domains are split so deployed agents never double up: the outreach monitor
 * owns follow-ups (`schedule_follow_up`), the system scanner owns revenue
 * (`create_task`), the ticket worker owns *status* (`set_ticket_status` —
 * escalating overdue work into progress), and this owns *ownership*
 * (`assign_ticket` — putting an unowned ticket on someone's plate).
 *
 * The honesty line: routing has to pick *someone*, and the one defensible
 * choice from the data alone is load — assign each unowned ticket to the human
 * with the fewest open tickets, balancing the queue. It deliberately routes
 * only to *humans* (assigning to an AI persona would imply the AI will do the
 * work, which it can't promise) and only touches *unowned* p0/p1 tickets (it
 * never reassigns work that already has an owner — that's a human call).
 */

import { queueApproval, listApprovals } from './approvals';
import { TICKET_OWNERS } from '../db/tickets';
import type { Ticket, TicketPriority, TicketStatus } from '../db/types';
import type { AssignTicketAction } from './types';

/** Terminal statuses carry no ownership burden — exclude from load + candidates. */
const TERMINAL: ReadonlySet<TicketStatus> = new Set<TicketStatus>([
  'done',
  'cancelled',
]);

/** Only the priorities worth routing proactively. p2/p3 can wait for a human. */
const ROUTABLE_PRIORITY: ReadonlySet<TicketPriority> = new Set<TicketPriority>([
  'p0',
  'p1',
]);

/**
 * The owners this router will route *to* — humans only, sorted for a stable
 * alphabetical tie-break. Derived from the canonical roster so it stays in sync;
 * `claude:*` personas are excluded on purpose (see the honesty line in the
 * module header).
 */
const HUMAN_OWNERS: readonly string[] = TICKET_OWNERS.filter(
  (o) => !o.startsWith('claude:'),
)
  .slice()
  .sort();

/** An unowned, non-terminal, high-priority ticket is a routing candidate. */
function isUnownedPriority(t: Ticket): boolean {
  if (t.owner) return false;
  if (TERMINAL.has(t.status)) return false;
  return ROUTABLE_PRIORITY.has(t.priority);
}

/**
 * Current open-ticket load per human owner, seeded at 0 so an idle owner is
 * still a valid (and preferred) routing target. Counts every non-terminal
 * ticket an owner already holds.
 */
function loadByOwner(tickets: Ticket[]): Map<string, number> {
  const load = new Map<string, number>(HUMAN_OWNERS.map((o) => [o, 0]));
  for (const t of tickets) {
    if (!t.owner || TERMINAL.has(t.status)) continue;
    if (load.has(t.owner)) load.set(t.owner, load.get(t.owner)! + 1);
  }
  return load;
}

/** The human currently carrying the fewest open tickets (alpha tie-break). */
function leastLoaded(load: Map<string, number>): string {
  let best = HUMAN_OWNERS[0];
  for (const o of HUMAN_OWNERS) {
    if ((load.get(o) ?? 0) < (load.get(best) ?? 0)) best = o;
  }
  return best;
}

/** p0 before p1, then by id, so routing order is deterministic across runs. */
function routingOrder(a: Ticket, b: Ticket): number {
  if (a.priority !== b.priority) return a.priority < b.priority ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export interface ProposeTaskRoutingResult {
  /** How many new assignment proposals were queued for approval. */
  proposed: number;
  /** Unowned tickets that already had a pending assignment (skipped). */
  skipped: number;
}

/**
 * Scan the live ticket queue and queue an `assign_ticket` approval for each
 * unowned, high-priority ticket, routing it to the least-loaded human owner.
 * The load count is incremented as we go so a batch spreads evenly instead of
 * dumping every ticket on the same person. De-dupes by ticketId against pending
 * assignment proposals so re-running doesn't pile duplicates. Returns how many
 * were proposed vs. skipped.
 */
export async function proposeTaskRouting(
  tickets: Ticket[],
): Promise<ProposeTaskRoutingResult> {
  if (HUMAN_OWNERS.length === 0) return { proposed: 0, skipped: 0 };

  const candidates = tickets.filter(isUnownedPriority).sort(routingOrder);

  // Don't propose a second assignment for a ticket that already has one waiting.
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'assign_ticket')
      .map((a) => (a.action as AssignTicketAction).ticketId),
  );

  const load = loadByOwner(tickets);

  let proposed = 0;
  let skipped = 0;
  for (const t of candidates) {
    if (alreadyQueued.has(t.id)) {
      skipped++;
      continue;
    }
    const owner = leastLoaded(load);
    const action: AssignTicketAction = {
      kind: 'assign_ticket',
      ticketId: t.id,
      ticketTitle: `${t.id} · ${t.title}`,
      owner,
      fromOwner: null,
    };
    await queueApproval(
      action,
      'scheduler',
      `Unowned ${t.priority.toUpperCase()} · ${t.status} — route to @${owner} (lightest load)`,
    );
    // Spread the batch: this owner now carries one more, and don't double-route
    // the same ticket within a run.
    load.set(owner, (load.get(owner) ?? 0) + 1);
    alreadyQueued.add(t.id);
    proposed++;
  }

  return { proposed, skipped };
}
